import { differenceInCalendarDays, format, isSameDay, isSameYear } from "date-fns";

class DateFormatConstants {
  static readonly CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
  static readonly ORDINAL_DATE = "do MMM, yyyy";
  static readonly ORDINAL_DAY_MONTH = "do MMM";
}

/**
 * Parses a YYYY-MM-DD string as local midnight on that calendar day.
 * - Returns null for non-ISO strings and impossible dates such as 2026-02-30.
 */
export const parseCalendarDate = (datePart: string): Date | null => {
  const match = DateFormatConstants.CALENDAR_DATE_PATTERN.exec(datePart);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
};

/** Formats a date as "20th Aug, 2026". */
export const formatOrdinalDate = (date: Date): string =>
  format(date, DateFormatConstants.ORDINAL_DATE);

/**
 * Formats a date range as "4th Aug – 4th Sep, 2026".
 * - Repeats the year on both ends only when they differ, and collapses a single day to one date.
 */
export const formatOrdinalDateRange = (start: Date, end: Date): string => {
  if (isSameDay(start, end)) return formatOrdinalDate(start);
  if (isSameYear(start, end)) {
    return `${format(start, DateFormatConstants.ORDINAL_DAY_MONTH)} – ${formatOrdinalDate(end)}`;
  }
  return `${formatOrdinalDate(start)} – ${formatOrdinalDate(end)}`;
};

/**
 * Returns the UTC calendar day of an instant as a local-midnight Date.
 * - Lets the local-time formatters below show the UTC day regardless of the host timezone.
 */
export const toUtcCalendarDate = (instant: Date): Date =>
  new Date(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate());

/**
 * Formats an instant range as "1st Oct, 2026, 22:00 UTC – 1st Nov, 2026, 22:00 UTC".
 * - Always writes both full dates, since the times make a collapsed range ambiguous.
 */
export const formatUtcDateTimeRange = (start: Date, end: Date): string =>
  `${formatUtcDateTime(start)} – ${formatUtcDateTime(end)}`;

const formatUtcDateTime = (instant: Date): string => {
  const hours = String(instant.getUTCHours()).padStart(2, "0");
  const minutes = String(instant.getUTCMinutes()).padStart(2, "0");
  return `${formatOrdinalDate(toUtcCalendarDate(instant))}, ${hours}:${minutes} UTC`;
};

/**
 * Counts the calendar days from start to an exclusive end, so Aug 4 – Sep 4 is 31.
 * - Throws RangeError when a date is invalid or end is before start, so a bad period never renders.
 */
export const countDaysBetween = (start: Date, end: Date): number => {
  const days = differenceInCalendarDays(end, start);
  if (!(days >= 0)) throw new RangeError("Period must be valid dates with end on or after start");
  return days;
};
